import React, { useCallback, useContext } from 'react';
import { useApolloClient } from '@apollo/client';
import { Route, Navigate, Routes, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { UserContext } from 'constants/user-context';
import { User } from 'lib/User';

import { Spying } from 'pages/Spying';
import { Settings } from 'pages/Settings/Settings';
import { UseDesktopWebApp } from 'pages/UseDesktopWebApp';

import { ErrorHandler } from 'utils/error-handler';

import { FourOhFour } from 'components/404';
import { Loading } from 'components/Loading';
import { NoAccessibleTotem } from 'components/NoAccessibleTotem';

import { getDefaultTotemPath, isBrokenTotemIdInUrl } from 'wrappers/center-panel-routing';

import { Overlay } from 'ui/Overlay';

import { isClient, getUrlParameters } from 'utils';
import { isMobile } from 'utils/isMobile';

import { CenterPanelStyle } from 'styles/wrappers/center-panel.styles';
import { TotemLoadingStyle } from 'styles/pages/totem.styles';

import {
    GetTotemsDocument,
    GetUserDocument,
    TotemFragmentFragment as TotemData,
    useGetTotemsQuery,
} from 'data/__generated__';
import { Totem } from 'pages/Totem/Totem';

// interface TotemsQueryData {
//     totemsOfUser: TotemData[];
// }

export const CenterPanel: React.FC = () => {
    const { loading, error, data: totemData } = useGetTotemsQuery();
    const { user, organization } = useContext(UserContext);
    const { pathname } = useLocation();
    const apolloClient = useApolloClient();

    // Deux lectures distinctes décident qu'un site est accessible : GET_TOTEMS (le site existe) et GET_USER
    // (`roles` autorise ce site). Juste après la création d'un compte, l'une comme l'autre peut être en retard,
    // donc on relance LES DEUX : ne relancer que GET_TOTEMS ne pouvait pas réparer des `roles` périmés.
    const retryAccessibleTotems = useCallback(() => {
        // Les erreurs éventuelles remontent déjà par le `error` du hook GET_TOTEMS ci-dessous.
        apolloClient.refetchQueries({ include: [GetUserDocument, GetTotemsDocument] }).catch(() => undefined);
    }, [apolloClient]);

    if (error) {
        throw error;
    }

    if (loading) {
        return (
            <TotemLoadingStyle>
                <Loading />
            </TotemLoadingStyle>
        );
    }

    const { totemId: totemIdToCheck } = getUrlParameters(pathname);
    // `totemsOfUser` returns every totem of the organization; keep only the ones the API will
    // actually authorize, otherwise the nav — and the default redirect below, which lands on
    // `totems[0]` — point at totems that answer `Accès refusé`.
    const totems: TotemData[] =
        totemData?.totemsOfUser?.filter(
            (totem): totem is TotemData => !!totem && User.canAccessTotem(totem._id ?? ''),
        ) ?? [];
    const totemToUse = totems.find((totem) => totem?._id === totemIdToCheck) || totems[0] || {};
    const { _id: totemId } = totemToUse as TotemData;

    // Route par défaut. Sans site accessible (compte tout juste créé dont le site n'est pas encore visible
    // par cette lecture, ou organisation sans site) il n'y a pas d'URL à construire : on affiche un état
    // d'attente avec « Réessayer » au lieu de router sur `/totem/undefined`.
    const defaultTotemPath = getDefaultTotemPath(totemId);
    const defaultTotemRoute = defaultTotemPath ? (
        <Navigate to={defaultTotemPath} replace />
    ) : (
        <NoAccessibleTotem onRetry={retryAccessibleTotems} />
    );

    const isToteam = !isClient();

    if (!user || !organization) {
        return null;
    }

    if (isMobile()) {
        return <UseDesktopWebApp />;
    }

    return (
        <DashboardContainer>
            <CenterPanelStyle>
                <ErrorHandler type="inner">
                    <Overlay name="popup-overlay" />
                    <Routes>
                        {/* default route is TOTEM n°1 */}
                        <Route path="/" element={defaultTotemRoute} />
                        <Route path="/totem" element={defaultTotemRoute} />
                        {isToteam && <Route path="/spying" element={<Spying />} />}
                        <Route
                            path="/totem/:totemId?/:navItem?/:orderId?/:productId?"
                            // Une id de site valide mais inaccessible reste un 404 (cf. `Totem.tsx`) ; seules
                            // les URLs `/totem/undefined` déjà en circulation sont rattrapées.
                            element={
                                isBrokenTotemIdInUrl(totemIdToCheck) ? defaultTotemRoute : <Totem totems={totems} />
                            }
                        />
                        <Route path="/settings/*" element={<Settings user={user} totems={totems as TotemData[]} />} />
                        <Route element={<FourOhFour />} />
                    </Routes>
                </ErrorHandler>
            </CenterPanelStyle>
        </DashboardContainer>
    );
};

const DashboardContainer = styled.div`
    flex: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: auto;
`;
